import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/PaymentProfilesService';
import { PaymentProfile } from '../types';

export const usePaymentProfile = (paymentProfileId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const paymentProfileQuery = useQuery({
        queryKey: ['payment-profile', paymentProfileId],
        queryFn: () => fetch(paymentProfileId!),
        enabled: !!paymentProfileId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: PaymentProfile) => {
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return add({ ...rest, created: new Date().toISOString() });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: PaymentProfile) => {
            if (!paymentProfileId) throw new Error('paymentProfileId is required parameter');
            const { createdByUsername: _createdByUsername, updated: _updated, ...rest } = values;
            return update(paymentProfileId, { ...rest, created: rest.created || new Date().toISOString() });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
            queryClient.invalidateQueries({ queryKey: ['payment-profile', paymentProfileId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!paymentProfileId) throw new Error('paymentProfileId is required parameter');
            return deleteRecord(paymentProfileId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
        }
    });

    return {
        record: paymentProfileQuery.data,
        loading: paymentProfileQuery.isLoading,
        fetchRecord: paymentProfileQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};